import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ar } from 'date-fns/locale';
import { whatsappSchedulerService, ScheduledMessage } from '../../services/whatsappSchedulerService';
import { WhatsAppMessageType, WhatsAppMessageData } from '../../services/hybridWhatsAppService';

const WhatsAppScheduler: React.FC = () => {
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newMessage, setNewMessage] = useState({
    messageType: 'deviceReceived' as WhatsAppMessageType,
    customerName: '',
    customerPhone: '',
    deviceType: '',
    deviceBrand: '',
    cost: '',
    orderId: '',
    scheduledAt: new Date(),
    repeatType: 'none' as 'none' | 'daily' | 'weekly' | 'monthly',
    repeatUntil: null as Date | null
  });

  useEffect(() => {
    loadScheduledMessages();
  }, []);

  const loadScheduledMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const messages = await whatsappSchedulerService.getAllScheduledMessages();
      setScheduledMessages(messages);
    } catch (err) {
      console.error('Error loading scheduled messages:', err);
      setError('فشل في تحميل الرسائل المجدولة');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleMessage = async () => {
    try {
      if (!newMessage.customerName || !newMessage.customerPhone) {
        setError('يرجى ملء جميع الحقول المطلوبة');
        return;
      }

      const messageData: WhatsAppMessageData = {
        customerName: newMessage.customerName,
        customerPhone: newMessage.customerPhone,
        deviceType: newMessage.deviceType,
        deviceBrand: newMessage.deviceBrand,
        cost: newMessage.cost,
        orderId: newMessage.orderId
      };

      await whatsappSchedulerService.scheduleMessage(
        newMessage.messageType,
        messageData,
        newMessage.scheduledAt,
        newMessage.repeatType,
        newMessage.repeatUntil || undefined
      );

      setOpenDialog(false);
      setNewMessage({
        messageType: 'deviceReceived',
        customerName: '',
        customerPhone: '',
        deviceType: '',
        deviceBrand: '',
        cost: '',
        orderId: '',
        scheduledAt: new Date(),
        repeatType: 'none',
        repeatUntil: null
      });
      
      await loadScheduledMessages();
    } catch (err) {
      console.error('Error scheduling message:', err);
      setError('فشل في جدولة الرسالة');
    }
  };

  const handleCancelMessage = async (messageId: string) => {
    try {
      await whatsappSchedulerService.cancelScheduledMessage(messageId);
      await loadScheduledMessages();
    } catch (err) {
      console.error('Error cancelling message:', err);
      setError('فشل في إلغاء الرسالة');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await whatsappSchedulerService.deleteScheduledMessage(messageId);
      await loadScheduledMessages();
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('فشل في حذف الرسالة');
    }
  };

  const handleSendNow = async (messageId: string) => {
    try {
      await whatsappSchedulerService.sendScheduledMessage(messageId);
      await loadScheduledMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      setError('فشل في إرسال الرسالة');
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'sent': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending': return 'في الانتظار';
      case 'sent': return 'تم الإرسال';
      case 'failed': return 'فشل';
      case 'cancelled': return 'ملغي';
      default: return status;
    }
  };

  const getMessageTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      deviceReceived: 'استلام الجهاز',
      deviceInProgress: 'أثناء العمل',
      deviceCompleted: 'اكتمال الإصلاح',
      deviceUrgent: 'تذكير عاجل'
    };
    return labels[type] || type;
  };

  const getRepeatTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      none: 'لا يتكرر',
      daily: 'يومياً',
      weekly: 'أسبوعياً',
      monthly: 'شهرياً'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              جاري تحميل الرسائل المجدولة...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ar}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            ⏰ الرسائل المجدولة
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{ mr: 1 }}
            >
              جدولة رسالة جديدة
            </Button>
            <IconButton onClick={loadScheduledMessages}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Messages Table */}
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>النوع</TableCell>
                    <TableCell>العميل</TableCell>
                    <TableCell>الموعد المجدول</TableCell>
                    <TableCell>التكرار</TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scheduledMessages.map((message) => (
                    <TableRow key={message.id}>
                      <TableCell>
                        <Chip
                          label={getMessageTypeLabel(message.messageType)}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {message.messageData.customerName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {message.messageData.customerPhone}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {message.scheduledAt.toLocaleDateString('ar-EG')} {message.scheduledAt.toLocaleTimeString('ar-EG')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getRepeatTypeLabel(message.repeatType || 'none')}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(message.status)}
                          color={getStatusColor(message.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {message.status === 'pending' && (
                            <>
                              <Tooltip title="إرسال الآن">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSendNow(message.id)}
                                >
                                  <SendIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="إلغاء">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleCancelMessage(message.id)}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="حذف">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteMessage(message.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {scheduledMessages.length === 0 && (
              <Box textAlign="center" py={4}>
                <ScheduleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  لا توجد رسائل مجدولة
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  اضغط على "جدولة رسالة جديدة" لإضافة رسالة مجدولة
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Schedule Message Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>جدولة رسالة جديدة</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>نوع الرسالة</InputLabel>
                  <Select
                    value={newMessage.messageType}
                    onChange={(e) => setNewMessage({ ...newMessage, messageType: e.target.value as WhatsAppMessageType })}
                  >
                    <MenuItem value="deviceReceived">استلام الجهاز</MenuItem>
                    <MenuItem value="deviceInProgress">أثناء العمل</MenuItem>
                    <MenuItem value="deviceCompleted">اكتمال الإصلاح</MenuItem>
                    <MenuItem value="deviceUrgent">تذكير عاجل</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="اسم العميل"
                  value={newMessage.customerName}
                  onChange={(e) => setNewMessage({ ...newMessage, customerName: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="رقم الهاتف"
                  value={newMessage.customerPhone}
                  onChange={(e) => setNewMessage({ ...newMessage, customerPhone: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="نوع الجهاز"
                  value={newMessage.deviceType}
                  onChange={(e) => setNewMessage({ ...newMessage, deviceType: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ماركة الجهاز"
                  value={newMessage.deviceBrand}
                  onChange={(e) => setNewMessage({ ...newMessage, deviceBrand: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="التكلفة"
                  value={newMessage.cost}
                  onChange={(e) => setNewMessage({ ...newMessage, cost: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="رقم الطلب"
                  value={newMessage.orderId}
                  onChange={(e) => setNewMessage({ ...newMessage, orderId: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="موعد الإرسال"
                  value={newMessage.scheduledAt}
                  onChange={(date) => setNewMessage({ ...newMessage, scheduledAt: date || new Date() })}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>نوع التكرار</InputLabel>
                  <Select
                    value={newMessage.repeatType}
                    onChange={(e) => setNewMessage({ ...newMessage, repeatType: e.target.value as any })}
                  >
                    <MenuItem value="none">لا يتكرر</MenuItem>
                    <MenuItem value="daily">يومياً</MenuItem>
                    <MenuItem value="weekly">أسبوعياً</MenuItem>
                    <MenuItem value="monthly">شهرياً</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {newMessage.repeatType !== 'none' && (
                <Grid item xs={12}>
                  <DateTimePicker
                    label="انتهاء التكرار"
                    value={newMessage.repeatUntil}
                    onChange={(date) => setNewMessage({ ...newMessage, repeatUntil: date })}
                    slotProps={{
                      textField: {
                        fullWidth: true
                      }
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>إلغاء</Button>
            <Button onClick={handleScheduleMessage} variant="contained">
              جدولة الرسالة
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default WhatsAppScheduler;
